import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import { SimilarProductItem, SimilarProductsSource } from '@core/models/models';

@Component({
  selector: 'app-similar-products',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page">
      <header class="top-bar">
        <div>
          <h2>מוצרים דומים</h2>
          <p class="tagline">הזן מזהה מוצר כדי למצוא עד 10 מוצרים דומים לפי AI</p>
        </div>
      </header>

      @if (errorMessage) {
        <p class="error" role="alert">{{ errorMessage }}</p>
      }

      <section class="card">
        <form class="search-row" (ngSubmit)="findSimilar()">
          <label>
            <span>מזהה מוצר</span>
            <input
              type="number"
              inputmode="numeric"
              min="1"
              step="1"
              data-testid="similar-product-id"
              [(ngModel)]="productIdInput"
              name="productId"
              placeholder="לדוגמה 12345"
              required
            />
          </label>
          <button type="submit" class="primary" data-testid="similar-search" [disabled]="loading">
            {{ loading ? 'מחפש…' : 'מצא דומים' }}
          </button>
        </form>
      </section>

      @if (source) {
        <section class="card source-card">
          <h3>מוצר מקור</h3>
          <div class="product-main">
            <strong>{{ source.displayName }}</strong>
            <span class="meta">מזהה: {{ source.productId }}</span>
            @if (source.chainName) {
              <span class="meta">רשת: {{ source.chainName }}</span>
            }
            @if (source.brandName) {
              <span class="meta">{{ source.brandName }}</span>
            }
            @if (source.barcode) {
              <span class="meta barcode">{{ source.barcode }}</span>
            }
          </div>
        </section>
      }

      @if (searched && !loading) {
        <section class="card">
          <h3>תוצאות ({{ items.length }})</h3>
          @if (items.length === 0) {
            <p class="muted">לא נמצאו מוצרים דומים. ייתכן שאין embedding למוצר זה.</p>
          } @else {
            <ol class="results">
              @for (item of items; track item.productId; let i = $index) {
                <li>
                  <span class="rank">{{ i + 1 }}</span>
                  <div class="product-main">
                    <strong>{{ item.displayName }}</strong>
                    <span class="meta">מזהה: {{ item.productId }}</span>
                    @if (item.chainName) {
                      <span class="meta">רשת: {{ item.chainName }}</span>
                    }
                    @if (item.brandName) {
                      <span class="meta">{{ item.brandName }}</span>
                    }
                    @if (item.barcode) {
                      <span class="meta barcode">{{ item.barcode }}</span>
                    }
                  </div>
                  <div class="similarity">
                    {{ formatSimilarity(item.similarity) }}
                  </div>
                </li>
              }
            </ol>
          }
        </section>
      }
    </div>
  `,
  styles: [
    `
      .page {
        max-width: 900px;
        margin: 0 auto;
        padding: 1rem 1rem 2rem;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .top-bar h2 {
        margin: 0 0 0.25rem;
        font-size: 1.15rem;
      }

      .tagline {
        margin: 0;
        color: #444;
        font-size: 0.95rem;
      }

      .card {
        background: #fff;
        border: 1px solid #e5e5e5;
        border-radius: 12px;
        padding: 1rem;
      }

      .search-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        align-items: flex-end;
      }

      label {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.85rem;
        color: #444;
        min-width: 12rem;
        flex: 1;
      }

      input {
        font: inherit;
        font-size: 1rem;
        padding: 0.55rem 0.7rem;
        border: 1px solid #ccc;
        border-radius: 8px;
      }

      .primary {
        font: inherit;
        padding: 0.55rem 1rem;
        border: none;
        border-radius: 8px;
        background: #0b57d0;
        color: #fff;
        cursor: pointer;
      }

      .primary:disabled {
        opacity: 0.65;
        cursor: default;
      }

      h3 {
        margin: 0 0 0.75rem;
        font-size: 1rem;
      }

      .product-main {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        min-width: 0;
      }

      .meta {
        font-size: 0.82rem;
        color: #555;
      }

      .barcode {
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      }

      .results {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .results > li {
        display: grid;
        grid-template-columns: 2rem 1fr auto;
        gap: 0.75rem;
        align-items: start;
        padding: 0.75rem;
        border: 1px solid #e5e5e5;
        border-radius: 8px;
      }

      .rank {
        font-weight: 700;
        color: #0b57d0;
      }

      .similarity {
        font-weight: 700;
        white-space: nowrap;
      }

      .muted {
        color: #666;
        margin: 0;
      }

      .error {
        margin: 0;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        background: #fdecea;
        color: #b00020;
      }
    `
  ]
})
export class SimilarProductsComponent {
  private readonly api = inject(ApiService);

  productIdInput: number | null = null;
  loading = false;
  searched = false;
  errorMessage: string | null = null;
  source: SimilarProductsSource | null = null;
  items: SimilarProductItem[] = [];

  async findSimilar(): Promise<void> {
    const productId = Number(this.productIdInput);
    if (!Number.isFinite(productId) || productId < 1) {
      this.errorMessage = 'הזן מזהה מוצר תקין.';
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.searched = false;

    try {
      const response = await firstValueFrom(this.api.findSimilarProducts(productId, 10));
      this.source = response.source;
      this.items = response.items;
      this.searched = true;
    } catch (err) {
      this.source = null;
      this.items = [];
      this.searched = false;
      this.errorMessage = this.readError(err);
    } finally {
      this.loading = false;
    }
  }

  formatSimilarity(value: number): string {
    return `${(value * 100).toFixed(1)}%`;
  }

  private readError(err: unknown): string {
    if (err && typeof err === 'object' && 'error' in err) {
      const body = (err as { error?: unknown }).error;
      if (body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string') {
        return (body as { error: string }).error;
      }
    }
    return 'משהו השתבש. נסה שוב.';
  }
}
